import { useQuery, useMutation, useQueryClient, queryOptions } from '@tanstack/react-query';
import { api } from '../api';
import { toast } from '../toast';
import { getGetSchoolPaymentDetailsQueryKey } from '../generated/sportSchool';
import type {
  SchoolResponse,
  CreateSchoolRequest,
  UpdateSchoolRequest,
  UpdateSchoolBrandingRequest,
  UpdateSchoolPaymentDetailsRequest,
  ProvisionSchoolRequest,
  ProvisionSchoolResponse,
  CreateSkillLevelRequest,
  UpdateSkillLevelRequest,
  ReorderSkillLevelsRequest,
  SkillLevel,
} from '@sportschool/shared-types';

export function useSchools() {
  return useQuery<SchoolResponse[]>({
    queryKey: ['schools'],
    queryFn: async () => {
      const res = await api.get('/schools');
      return res.data;
    },
  });
}

export function useMySchools() {
  return useQuery<SchoolResponse[]>({
    queryKey: ['my-schools'],
    queryFn: async () => {
      const res = await api.get('/schools/me', { skipAuthRedirect: true });
      return res.data;
    },
  });
}

/** Query options of `useSchool` — shared with the offline prefetch so the cache keys cannot drift. */
export function schoolQueryOptions(schoolId: string) {
  return queryOptions({
    queryKey: ['schools', schoolId],
    queryFn: async (): Promise<SchoolResponse> => {
      const res = await api.get(`/schools/${schoolId}`);
      return res.data;
    },
    enabled: !!schoolId,
    retry: false,
  });
}

export function useSchool(schoolId: string) {
  return useQuery(schoolQueryOptions(schoolId));
}

export function useProvisionSchool() {
  const qc = useQueryClient();
  return useMutation<ProvisionSchoolResponse, Error, ProvisionSchoolRequest>({
    mutationFn: async (data) => {
      const res = await api.post('/schools/provision', data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schools'] });
      toast.success('School created successfully. An invitation email has been sent to the admin.');
    },
    onError: () => toast.error('Failed to create school. Please try again.'),
  });
}

export { useGetSchoolCreationAllowed } from '../generated/sportSchool';
export {
  useListSchoolDomains,
  useRegisterSchoolDomain,
  useVerifySchoolDomain,
  useDeleteSchoolDomain,
} from '../generated/sportSchool';

export function useCreateSchool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSchoolRequest) => {
      const res = await api.post('/schools', data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schools'] });
      toast.success('School created');
    },
    onError: () => toast.error('Failed to create school'),
  });
}

export function useUpdateSchool(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateSchoolRequest) => {
      const res = await api.put(`/schools/${schoolId}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schools'] });
      qc.invalidateQueries({ queryKey: ['schools', schoolId] });
      toast.success('Settings saved');
    },
    onError: () => toast.error('Failed to save settings'),
  });
}

export function useUpdateBranding(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateSchoolBrandingRequest) => {
      const res = await api.patch(`/schools/${schoolId}/branding`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schools', schoolId] });
      toast.success('Branding saved');
    },
    onError: () => toast.error('Failed to save branding'),
  });
}
export function useUpdatePaymentDetails(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateSchoolPaymentDetailsRequest) => {
      const res = await api.put(`/schools/${schoolId}/payment-details`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: getGetSchoolPaymentDetailsQueryKey(schoolId) });
      qc.invalidateQueries({ queryKey: ['schools', schoolId] });
      toast.success('Dane płatności zostały zapisane');
    },
    onError: () => toast.error('Nie udało się zapisać danych płatności'),
  });
}

export function useSelfJoinSchool(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post(`/schools/${schoolId}/members/join`, null, {
        skipAuthRedirect: true,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schools'] });
      qc.invalidateQueries({ queryKey: ['members', schoolId] });
    },
  });
}

// ── Skill Levels ────────────────────────────────────────────────────────────────

/** Query options of `useSkillLevels` — shared with the offline prefetch. */
export function skillLevelsQueryOptions(schoolId: string) {
  return queryOptions({
    queryKey: ['skill-levels', schoolId],
    queryFn: async (): Promise<SkillLevel[]> => {
      const res = await api.get(`/schools/${schoolId}/skill-levels`);
      return res.data;
    },
    enabled: !!schoolId,
  });
}

export function useSkillLevels(schoolId: string) {
  return useQuery(skillLevelsQueryOptions(schoolId));
}

export function useCreateSkillLevel(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSkillLevelRequest) => {
      const res = await api.post(`/schools/${schoolId}/skill-levels`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skill-levels', schoolId] });
      toast.success('Skill level created');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to create skill level', code, args);
    },
  });
}

export function useUpdateSkillLevel(schoolId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateSkillLevelRequest) => {
      const res = await api.put(`/schools/${schoolId}/skill-levels/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skill-levels', schoolId] });
      toast.success('Skill level updated');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to update skill level', code, args);
    },
  });
}

export function useDeleteSkillLevel(schoolId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/schools/${schoolId}/skill-levels/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skill-levels', schoolId] });
      toast.success('Skill level deleted');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to delete skill level', code, args);
    },
  });
}

export function useReorderSkillLevels(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: ReorderSkillLevelsRequest) => {
      const res = await api.post(`/schools/${schoolId}/skill-levels/reorder`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skill-levels', schoolId] });
      toast.success('Skill levels order updated');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to update order', code, args);
    },
  });
}
